// Or from '@reduxjs/toolkit/query' if not using the auto-generated hooks
import {createApi, fetchBaseQuery, retry} from '@reduxjs/toolkit/query/react';
import {RootState} from '../store/store';
import AuthProvider from '../../AuthProviders/OIDCProvider';

/**
 * Base query with intelligent retry bailout logic
 *
 * Retries on:
 * - Network errors (FETCH_ERROR, TIMEOUT_ERROR)
 * - Server errors (500-599)
 * - Parsing errors (PARSING_ERROR)
 *
 * Bails out immediately on:
 * - Authentication errors (401, 403)
 * - Client errors (400-499) - validation, bad request, not found, etc.
 */
export const baseQueryWithRetryBailout = retry(
    async (args, api, extraOptions) => {
        const result = await fetchBaseQuery({
            baseUrl: import.meta.env.VITE_BACKEND_HOST + '/',
            prepareHeaders: async (headers, {getState}) => {
                const token = await AuthProvider.getToken();
                if (token) {
                    headers.set('authorization', `Bearer ${token}`);
                }
                return headers;
            },
        })(args, api, extraOptions);

        // Bail out of retries for client errors - retrying won't help
        if (result.error) {
            const status = result.error.status;

            // Authentication/authorization errors - session expired, no retries will fix this
            if (status === 401 || status === 403) {
                retry.fail(result.error);
            }

            // Client errors (400-499) - bad request, validation, not found, etc.
            // These are deterministic failures that won't be fixed by retrying
            if (typeof status === 'number' && status >= 400 && status < 500) {
                retry.fail(result.error);
            }
        }

        return result;
    },
    {
        maxRetries: 3,
        // RTK Query handles exponential backoff automatically
    }
);

// initialize an empty api service that we'll inject endpoints into later as needed
export const emptySplitApi = createApi({
    baseQuery: baseQueryWithRetryBailout,
    refetchOnMountOrArgChange: 120,
    endpoints: () => ({}),
});
